import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useGetXeroTrialBalanceSyncJobs,
  useQueueXeroTrialBalanceSync,
} from '~/hooks/apis/reporting/useXeroTrialBalanceSync';

import type {
  XeroTrialBalanceCompany as ReportingXeroTrialBalanceCompany,
  XeroTrialBalanceSyncJobStatus,
} from '~/services/ReportingService';
import { formatDateISO, startOfMonth, subMonths } from '~/services/TimeService';

const XERO_TRIAL_BALANCE_SYNC_STORAGE_KEY = 'xero-trial-balance-sync-jobs';
const XERO_COMPANIES: Array<ReportingXeroTrialBalanceCompany> = ['LE', 'LED', 'LP'];
const TERMINAL_STATUSES = new Set(['completed', 'failed']);

type SyncJobRecord = {
  jobId: string;
  queue: string;
  startAt: string;
  endAt: string;
  company: ReportingXeroTrialBalanceCompany | string;
  requestedAt: number;
  status?: string;
  progress?: number;
  attemptsMade?: number;
  createdAt?: number;
  processedAt?: number | null;
  finishedAt?: number | null;
  error?: string;
  lastCheckedAt?: number;
};

type FormState = {
  startAt: string;
  endAt: string;
  company: ReportingXeroTrialBalanceCompany;
};

const defaultFormState: FormState = {
  startAt: formatDateISO(startOfMonth(subMonths(1))),
  endAt: formatDateISO(startOfMonth()),
  company: 'LE',
};

function readStoredJobs(): Array<SyncJobRecord> {
  try {
    if (typeof window === 'undefined') return [];

    const storedValue = window.localStorage.getItem(XERO_TRIAL_BALANCE_SYNC_STORAGE_KEY);
    if (!storedValue) return [];

    const parsedValue = JSON.parse(storedValue);
    if (!Array.isArray(parsedValue)) return [];

    return parsedValue.filter((job): job is SyncJobRecord => Boolean(job?.jobId && job?.startAt && job?.endAt));
  } catch {
    return [];
  }
}

function writeStoredJobs(jobs: Array<SyncJobRecord>): void {
  try {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(XERO_TRIAL_BALANCE_SYNC_STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    // Job tracking is an admin convenience only; the sync is already queued server-side.
  }
}

function mergeJobStatus(job: SyncJobRecord, status: XeroTrialBalanceSyncJobStatus): SyncJobRecord {
  return {
    ...job,
    queue: status.queue,
    startAt: status.data.start_at,
    endAt: status.data.end_at,
    company: status.data.company,
    status: status.status,
    progress: status.progress,
    attemptsMade: status.attemptsMade,
    createdAt: status.createdAt,
    processedAt: status.processedAt,
    finishedAt: status.finishedAt,
    error: status.error,
    lastCheckedAt: Date.now(),
  };
}

function hasJobStatusChanged(job: SyncJobRecord, status: XeroTrialBalanceSyncJobStatus): boolean {
  return (
    job.queue !== status.queue ||
    job.startAt !== status.data.start_at ||
    job.endAt !== status.data.end_at ||
    job.company !== status.data.company ||
    job.status !== status.status ||
    job.progress !== status.progress ||
    job.attemptsMade !== status.attemptsMade ||
    job.createdAt !== status.createdAt ||
    job.processedAt !== status.processedAt ||
    job.finishedAt !== status.finishedAt ||
    job.error !== status.error
  );
}

function formatTimestamp(value?: number | null): string {
  if (!value) return '-';

  return new Date(value).toLocaleString();
}

function getStatusColor(status?: string): 'default' | 'info' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'active':
      return 'info';
    case 'waiting':
    case 'delayed':
    case 'paused':
      return 'warning';
    default:
      return 'default';
  }
}

function getProgressValue(progress?: number): number {
  if (typeof progress !== 'number' || Number.isNaN(progress)) return 0;

  return Math.min(100, Math.max(0, progress));
}

export default function ExportXeroTrialBalanceSyncForm() {
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [jobs, setJobs] = useState<Array<SyncJobRecord>>(() => readStoredJobs());
  const queueSync = useQueueXeroTrialBalanceSync();
  const jobStatusQueries = useGetXeroTrialBalanceSyncJobs(jobs.map((job) => job.jobId));
  const statusUpdateKey = jobStatusQueries.map((query) => `${query.dataUpdatedAt}:${query.errorUpdatedAt}`).join('|');

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === XERO_TRIAL_BALANCE_SYNC_STORAGE_KEY) {
        setJobs(readStoredJobs());
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => window.removeEventListener('storage', handleStorageEvent);
  }, []);

  useEffect(() => {
    const statusByJobId = new Map<string, XeroTrialBalanceSyncJobStatus>();

    jobStatusQueries.forEach((query, index) => {
      const jobId = jobs[index]?.jobId;
      if (jobId && query.data) {
        statusByJobId.set(jobId, query.data);
      }
    });

    if (statusByJobId.size === 0) return;

    setJobs((currentJobs) => {
      let hasChanged = false;
      const nextJobs = currentJobs.map((job) => {
        const status = statusByJobId.get(job.jobId);
        if (!status) return job;

        const nextJob = mergeJobStatus(job, status);
        hasChanged = hasChanged || hasJobStatusChanged(job, status);
        return nextJob;
      });

      if (hasChanged) {
        writeStoredJobs(nextJobs);
        return nextJobs;
      }

      return currentJobs;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statusUpdateKey captures query changes; state updater avoids stale jobs.
  }, [statusUpdateKey]);

  const hasActiveJobs = useMemo(() => jobs.some((job) => !TERMINAL_STATUSES.has(job.status ?? '')), [jobs]);

  const formError = useMemo(() => {
    if (!formState.startAt || !formState.endAt) return 'Start and end dates are required.';
    if (formState.endAt < formState.startAt) return 'End date must be after the start date.';

    return null;
  }, [formState.endAt, formState.startAt]);

  const updateStoredJobs = useCallback((nextJobs: Array<SyncJobRecord>) => {
    writeStoredJobs(nextJobs);
    setJobs(nextJobs);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (formError) return;

      try {
        const response = await queueSync.mutateAsync(formState);
        const nextJob: SyncJobRecord = {
          jobId: response.jobId,
          queue: response.queue,
          startAt: formState.startAt,
          endAt: formState.endAt,
          company: formState.company,
          requestedAt: Date.now(),
          status: 'waiting',
          progress: 0,
        };
        const nextJobs = [nextJob, ...jobs.filter((job) => job.jobId !== response.jobId)];

        updateStoredJobs(nextJobs);
        enqueueSnackbar(`Xero trial balance sync queued for ${formState.company}.`, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(
          `Failed to queue Xero trial balance sync: ${error instanceof Error ? error.message : 'Unknown error'}`,
          {
            variant: 'error',
          },
        );
      }
    },
    [enqueueSnackbar, formError, formState, jobs, queueSync, updateStoredJobs],
  );

  const handleRemoveJob = useCallback(
    (jobId: string) => {
      updateStoredJobs(jobs.filter((job) => job.jobId !== jobId));
    },
    [jobs, updateStoredJobs],
  );

  const handleClearFinishedJobs = useCallback(() => {
    updateStoredJobs(jobs.filter((job) => !TERMINAL_STATUSES.has(job.status ?? '')));
  }, [jobs, updateStoredJobs]);

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Queue a background sync for a Xero company and monitor the Bull job returned by svc-reporting.
      </Alert>

      <Box component="form" onSubmit={handleSubmit}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
          <TextField
            required
            type="date"
            label="Select Start Date"
            value={formState.startAt}
            onChange={(event) => setFormState((current) => ({ ...current, startAt: event.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            required
            type="date"
            label="Select End Date"
            value={formState.endAt}
            onChange={(event) => setFormState((current) => ({ ...current, endAt: event.target.value }))}
            error={Boolean(formError)}
            helperText={formError}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl>
            <FormLabel>Company</FormLabel>
            <RadioGroup
              row
              value={formState.company}
              onChange={(event) =>
                setFormState((current) => ({
                  ...current,
                  company: event.target.value as ReportingXeroTrialBalanceCompany,
                }))
              }
            >
              {XERO_COMPANIES.map((company) => (
                <FormControlLabel key={company} value={company} control={<Radio />} label={company} />
              ))}
            </RadioGroup>
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SyncIcon />}
            disabled={Boolean(formError) || queueSync.isPending}
          >
            {queueSync.isPending ? 'Syncing...' : 'Sync trial balance'}
          </Button>
        </Stack>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Sync jobs</Typography>
        <Button
          variant="text"
          disabled={!jobs.some((job) => TERMINAL_STATUSES.has(job.status ?? ''))}
          onClick={handleClearFinishedJobs}
        >
          Clear finished jobs
        </Button>
      </Stack>

      {jobs.length === 0 ? (
        <Alert severity="info">No Xero trial balance sync jobs are tracked in this browser.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          {hasActiveJobs && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Date range</TableCell>
                <TableCell>Job ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Finished</TableCell>
                <TableCell>Error</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job, index) => {
                const progress = getProgressValue(job.progress);
                const query = jobStatusQueries[index];

                return (
                  <TableRow key={job.jobId}>
                    <TableCell>{job.company}</TableCell>
                    <TableCell>
                      {job.startAt} to {job.endAt}
                    </TableCell>
                    <TableCell>{job.jobId}</TableCell>
                    <TableCell>
                      <Chip size="small" color={getStatusColor(job.status)} label={job.status ?? 'unknown'} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Stack spacing={0.5}>
                        <LinearProgress variant="determinate" value={progress} />
                        <Typography variant="caption" color="text.secondary">
                          {progress}%
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatTimestamp(job.requestedAt)}</TableCell>
                    <TableCell>{formatTimestamp(job.finishedAt)}</TableCell>
                    <TableCell>{job.error ?? (query?.isError ? query.error?.message : '-')}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Refresh status">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => query?.refetch()}
                            disabled={!query || query.isFetching}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove from this browser">
                        <IconButton size="small" onClick={() => handleRemoveJob(job.jobId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
